import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { TextBausteinPicker } from '@/components/text-baustein-picker';
import { Loader2, Upload, X, Save, Building2, FileText, Image as ImageIcon, Type, Settings as SettingsIcon, MapPin, Mail, CreditCard, Receipt } from 'lucide-react';

interface PDFSettings {
  companyInfo?: string;
  companyName?: string;
  companyStreet?: string;
  companyCity?: string;
  companyPostalCode?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyVatId?: string;
  companyTaxId?: string;
  companyBankName?: string;
  companyBankIban?: string;
  companyBankBic?: string;
  companyCountry?: string;
  footerLeft?: string;
  footerCenter?: string;
  footerRight?: string;
  agbText?: string;
  logoFilename?: string;
  logoUrl?: string;
  fontFamily?: string;
}

interface SettingsModalProps {
  trigger?: React.ReactNode;
  /**
   * Controlled-Open-Zustand (Welle 1.5 UX-Entlastung — Kopfzeile entstapeln):
   * Im gebündelten Konto-Menü wird der Dialog als GESCHWISTER des DropdownMenu
   * gemountet und über diese Props gesteuert; die Komponente rendert dann
   * KEINEN eigenen Trigger (der Menü-Eintrag lebt im Parent, gleiches Muster
   * wie GAEBExport/X84PriceImport im Export-Menü, siehe quote-sidebar.tsx).
   * Ohne `open` (Default) bleibt der eigenständige Trigger-Button + interner
   * Open-State erhalten (Rückwärtskompatibilität für andere Aufrufer).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function SettingsModal({ trigger, open: openProp, onOpenChange: onOpenChangeProp }: SettingsModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) onOpenChangeProp?.(next);
    else setInternalOpen(next);
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  
  const [settings, setSettings] = useState<PDFSettings>({
    companyInfo: '',
    footerLeft: '',
    footerCenter: '',
    footerRight: '',
    agbText: '',
    fontFamily: 'helvetica'
  });

  // Load settings when modal opens
  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/pdf-settings');
      const data = await response.json();
      
      if (data.success && data.data) {
        setSettings(prev => ({
          ...prev,
          ...data.data,
          fontFamily: data.data.fontFamily ?? 'helvetica'
        }));
        
        if (data.data.logoFilename) {
          setLogoPreview('/api/pdf-settings/logo');
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch('/api/pdf-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Gespeichert",
          description: "PDF Export-Einstellungen wurden gespeichert",
        });
        setOpen(false);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Fehler beim Speichern",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wählen Sie ein Bild aus",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch('/api/pdf-settings/logo', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        setLogoPreview(URL.createObjectURL(file));
        setSettings(prev => ({ ...prev, logoFilename: data.data.filename }));
        toast({
          title: "Logo hochgeladen",
          description: "Ihr Firmenlogo wurde gespeichert",
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      toast({
        title: "Upload fehlgeschlagen",
        description: error instanceof Error ? error.message : "Fehler beim Hochladen",
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const response = await fetch('/api/pdf-settings/logo', {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        setLogoPreview(null);
        setSettings(prev => ({ ...prev, logoFilename: undefined }));
        toast({
          title: "Logo gelöscht",
          description: "Das Firmenlogo wurde entfernt",
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Logo konnte nicht gelöscht werden",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" className="gap-1.5">
              <SettingsIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Einstellungen</span>
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            PDF Export-Einstellungen
          </DialogTitle>
          <DialogDescription>
            Konfigurieren Sie Ihr Firmenlogo, AGBs und weitere Metadaten für PDF-Exporte
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="logo" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="logo" className="text-xs">
                <ImageIcon className="h-3 w-3 mr-1" />
                Logo
              </TabsTrigger>
              <TabsTrigger value="company" className="text-xs">
                <Building2 className="h-3 w-3 mr-1" />
                Firma
              </TabsTrigger>
              <TabsTrigger value="footer" className="text-xs">
                <FileText className="h-3 w-3 mr-1" />
                Fußzeile
              </TabsTrigger>
              <TabsTrigger value="agb" className="text-xs">
                <FileText className="h-3 w-3 mr-1" />
                AGB
              </TabsTrigger>
            </TabsList>

            {/* Logo Tab */}
            <TabsContent value="logo" className="space-y-4">
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <div className="relative">
                    <img 
                      src={logoPreview} 
                      alt="Company Logo" 
                      className="h-20 w-auto object-contain border rounded p-2"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0"
                      onClick={handleDeleteLogo}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-20 w-20 border-2 border-dashed rounded flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="hidden"
                    id="logo-upload-modal"
                  />
                  <Button 
                    onClick={() => document.getElementById('logo-upload-modal')?.click()}
                    disabled={uploadingLogo} 
                    size="sm"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Hochladen...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Logo hochladen
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500">Wird oben links im PDF angezeigt</p>
                </div>
              </div>

              {/* Font Selection */}
              <div className="space-y-2 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  <Label htmlFor="fontFamily">Schriftart</Label>
                </div>
                <Select
                  value={settings.fontFamily || 'helvetica'}
                  onValueChange={(value) => setSettings(prev => ({ ...prev, fontFamily: value }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Schriftart wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="helvetica">Helvetica (Sans-Serif)</SelectItem>
                    <SelectItem value="cambria">Cambria (Serif)</SelectItem>
                    <SelectItem value="times">Times New Roman</SelectItem>
                    <SelectItem value="courier">Courier (Monospace)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* Company Tab */}
            <TabsContent value="company" className="space-y-4">
              <div>
                <Label htmlFor="companyName">Firmenname</Label>
                <Input
                  id="companyName"
                  value={settings.companyName || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="z.B. Fortuna Bautec GmbH"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="companyInfo">Firmeninformationen (PDF-Kopfzeile)</Label>
                <Textarea
                  id="companyInfo"
                  value={settings.companyInfo || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, companyInfo: e.target.value }))}
                  placeholder="Fortuna Bautec GmbH&#10;T: +49 (0)30 / 499 622 66&#10;E: info@fortuna-bautec.de"
                  rows={3}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Freitext für PDF-Header (jede Zeile = ein Eintrag)</p>
              </div>

              {/* Structured fields for invoices */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MapPin className="h-4 w-4" />
                  Adresse
                </div>
                <div className="space-y-2">
                  <Input
                    placeholder="Straße & Hausnummer"
                    value={settings.companyStreet || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyStreet: e.target.value }))}
                  />
                  <div className="grid grid-cols-[100px_1fr] gap-2">
                    <Input
                      placeholder="PLZ"
                      value={settings.companyPostalCode || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyPostalCode: e.target.value }))}
                    />
                    <Input
                      placeholder="Stadt"
                      value={settings.companyCity || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyCity: e.target.value }))}
                    />
                  </div>
                  <Input
                    placeholder="Land (optional, z.B. DE)"
                    value={settings.companyCountry || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyCountry: e.target.value }))}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4" />
                  Kontakt
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="E-Mail"
                    value={settings.companyEmail || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyEmail: e.target.value }))}
                  />
                  <Input
                    placeholder="Telefon"
                    value={settings.companyPhone || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyPhone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Receipt className="h-4 w-4" />
                  Steuerdaten
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="USt-IdNr. (z.B. DE123456789)"
                    value={settings.companyVatId || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyVatId: e.target.value }))}
                  />
                  <Input
                    placeholder="Steuernummer"
                    value={settings.companyTaxId || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyTaxId: e.target.value }))}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CreditCard className="h-4 w-4" />
                  Bankverbindung
                </div>
                <div className="space-y-2">
                  <Input
                    placeholder="Bankname"
                    value={settings.companyBankName || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, companyBankName: e.target.value }))}
                  />
                  <div className="grid grid-cols-[1fr_140px] gap-2">
                    <Input
                      placeholder="IBAN"
                      className="font-mono text-sm"
                      value={settings.companyBankIban || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyBankIban: e.target.value }))}
                    />
                    <Input
                      placeholder="BIC"
                      className="font-mono text-sm"
                      value={settings.companyBankBic || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, companyBankBic: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Footer Tab */}
            <TabsContent value="footer" className="space-y-4">
              <div>
                <Label htmlFor="footerLeft">Linke Spalte</Label>
                <Textarea
                  id="footerLeft"
                  value={settings.footerLeft || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, footerLeft: e.target.value }))}
                  placeholder="Sitz der Gesellschaft:&#10;Lothar-Bucher-Str. 5a&#10;12157 Berlin"
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="footerCenter">Mittlere Spalte</Label>
                <Textarea
                  id="footerCenter"
                  value={settings.footerCenter || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, footerCenter: e.target.value }))}
                  placeholder="Amtsgericht Charlottenburg HRB 181638 B&#10;USt.-IdNr.: DE309712283"
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="footerRight">Rechte Spalte</Label>
                <Textarea
                  id="footerRight"
                  value={settings.footerRight || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, footerRight: e.target.value }))}
                  placeholder="Berliner Volksbank&#10;IBAN: DE04 1009 0000 2804 4240 00"
                  rows={3}
                  className="mt-1"
                />
              </div>
            </TabsContent>

            {/* AGB Tab */}
            <TabsContent value="agb" className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="agbText">Allgemeine Geschäftsbedingungen</Label>
                  <TextBausteinPicker
                    kategorie="schlusstext"
                    currentValue={settings.agbText || ''}
                    onInsert={(value) => setSettings(prev => ({ ...prev, agbText: value }))}
                  />
                </div>
                <Textarea
                  id="agbText"
                  value={settings.agbText || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, agbText: e.target.value }))}
                  placeholder="1. Zahlungsbedingungen...&#10;2. Lieferung und Leistung...&#10;3. Gewährleistung..."
                  rows={10}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Wird auf der letzten Seite des PDFs angezeigt</p>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Save Button + Weg zur vollen Einstellungsseite.
            Die Seite /settings (u.a. Angebotsnummern-Kreis, Briefkopf-Profile)
            war bisher von NIRGENDS in der App verlinkt — dieser Link ist der
            einzige Einstieg und gehört deshalb genau hierher: Wer Einstellungen
            sucht, landet über das Konto-Menü zuerst in diesem Dialog. */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="link"
            className="px-0 text-sm text-blue-600"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
            data-testid="link-settings-page"
          >
            Alle Einstellungen: Angebotsnummern, Briefkopf-Profile ...
          </Button>
          <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Speichern
              </>
            )}
          </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
